import { getApiErrorMessage } from '@/shared/api/apiError';
import { consumeSessionEndMessage } from '@/shared/api/authSession';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { SUPPORT } from '@/shared/constants/support';
import { Button, Icon, TextField } from '@/shared/ui';
import { selectIsAuthenticated, useAuthStore } from '@/stores/authStore';
import { Navigate, useNavigate } from '@tanstack/react-router';
import { Eye } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { LoginIllustration } from './LoginIllustration';

/**
 * 01 로그인 — 루다링크가 발급한 계정으로만 로그인 (회원가입 없음).
 * 왼쪽 일러스트 + 오른쪽 폼 · 고객센터. 성공 시 05 업체로 이동한다.
 */
export function LoginPage() {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const login = useAuthStore((state) => state.login);
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  /** 로그인 실패 문구 — 두 입력칸을 오류 상태로 바꾼다 */
  const [error, setError] = useState<string | null>(null);
  /** 세션 만료 등 안내 문구 — 입력칸은 정상 상태 */
  const [notice, setNotice] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 세션 만료로 튕겨 온 경우 안내 문구를 한 번 보여 준다
  useEffect(() => {
    setNotice(consumeSessionEndMessage());
  }, []);

  if (isAuthenticated) {
    return <Navigate to={ROUTE_PATHS.USERS} />;
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setIsSubmitting(true);
    try {
      await login(email, password);
      navigate({ to: ROUTE_PATHS.USERS });
    } catch (loginError) {
      // 서버 문구: 불일치(401) · 5회 실패 차단(429) · 이용 중지(403)
      setError(
        getApiErrorMessage(
          loginError,
          '로그인에 실패했습니다. 잠시 후 다시 시도해 주세요.',
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // 다시 입력하면 오류 표시를 지운다
  const clearError = () => setError(null);
  const hasError = error !== null;

  return (
    <div className="grid min-h-screen bg-surface-grey lg:grid-cols-2">
      {/* 좁은 화면에서는 일러스트를 숨기고 폼만 보인다 */}
      <div className="hidden p-6 lg:flex">
        <LoginIllustration />
      </div>

      <div className="flex flex-col items-center justify-between gap-10 px-6 pt-20 pb-10 lg:px-[120px] lg:pt-[200px]">
        <form
          onSubmit={handleSubmit}
          className="flex w-full max-w-[480px] flex-col gap-6"
        >
          <h1 className="text-heading-28 font-bold text-fg-primary">로그인</h1>
          {notice && (
            <p className="text-body-14 font-regular text-fg-tertiary">
              {notice}
            </p>
          )}
          <TextField
            label="이메일"
            type="email"
            autoComplete="username"
            maxLength={255}
            placeholder="name@company.com"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              clearError();
            }}
            error={hasError}
            required
          />
          <TextField
            label="비밀번호"
            type={isPasswordVisible ? 'text' : 'password'}
            autoComplete="current-password"
            maxLength={128}
            placeholder="비밀번호를 입력해주세요"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              clearError();
            }}
            error={error ?? false}
            required
            trailing={
              <button
                type="button"
                aria-label={
                  isPasswordVisible ? '비밀번호 숨기기' : '비밀번호 보기'
                }
                aria-pressed={isPasswordVisible}
                className="shrink-0 cursor-pointer"
                onClick={() => setPasswordVisible((visible) => !visible)}
              >
                {isPasswordVisible ? (
                  // 보이는 상태 아이콘은 디자인에 없어 같은 굵기·색의 lucide 아이콘을 쓴다
                  <Eye className="size-6 text-fg-light" strokeWidth={1.5} />
                ) : (
                  <Icon name={hasError ? 'eyeOffError' : 'eyeOff'} size={24} />
                )}
              </button>
            }
          />
          <Button type="submit" isLoading={isSubmitting} className="w-full">
            로그인
          </Button>
        </form>

        <SupportFooter />
      </div>
    </div>
  );
}

/** Figma 로그인 하단 — 고객센터 운영 시간 · 전화 · 이메일 · 카카오톡 · 홈페이지 */
function SupportFooter() {
  const linkClassName = 'hover:text-fg-tertiary hover:underline';
  return (
    <footer className="flex w-full max-w-[480px] flex-wrap items-center gap-x-6 gap-y-2 text-body-13 font-regular whitespace-nowrap text-fg-light">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span>{SUPPORT.hours}</span>
        <a
          href={`tel:${SUPPORT.phone.replace(/-/g, '')}`}
          className={linkClassName}
        >
          {SUPPORT.phone}
        </a>
        <a href={`mailto:${SUPPORT.email}`} className={linkClassName}>
          {SUPPORT.email}
        </a>
        {SUPPORT.kakaoChatUrl && (
          <ExternalLink href={SUPPORT.kakaoChatUrl}>카카오톡</ExternalLink>
        )}
      </div>
      <ExternalLink href={SUPPORT.homepageUrl}>홈페이지</ExternalLink>
    </footer>
  );
}

function ExternalLink({ href, children }: { href: string; children: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-0.5 hover:text-fg-tertiary hover:underline"
    >
      {children}
      <Icon name="circleArrow" size={14} />
    </a>
  );
}
